import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { MapPin, Calendar, Clock, ArrowLeft, Check, ChevronDown, ChevronUp, Users } from "lucide-react";
import PackageCard from "@/components/PackageCard";
import EnquiryForm from "@/components/EnquiryForm";
import EventCard from "@/components/EventCard";
import CheckoutModal from "@/components/CheckoutModal";
import { useLang } from "@/contexts/LanguageContext";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetailPage() {
  const { t } = useLang();
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [checkoutPkg, setCheckoutPkg] = useState(null);
  const [checkoutPkgIndex, setCheckoutPkgIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventRes, allRes] = await Promise.all([
          axios.get(`${API}/events/${slug}`),
          axios.get(`${API}/events/featured`),
        ]);
        setEvent(eventRes.data);
        setRelatedEvents(allRes.data.filter(e => e.slug !== slug).slice(0, 3));
        const q = {};
        eventRes.data.packages?.forEach((_, i) => { q[i] = 1; });
        setQuantities(q);
      } catch (err) {
        devWarn("Failed to fetch event:", err.message);
      } finally { setLoading(false); }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const handlePackageSelect = (pkg, method, pkgIndex) => {
    setCheckoutPkg(pkg);
    setCheckoutPkgIndex(pkgIndex);
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;
  if (!event) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-center px-6"><div><h2 className="font-['Cormorant_Garamond'] text-3xl text-white mb-4">{t("event_not_found")}</h2><Link to="/" className="text-[#C5A059] font-['Outfit'] text-sm hover:underline">{t("return_homepage")}</Link></div></div>;

  return (
    <main data-testid="event-detail-page" className="bg-[#0A0A0A]">
      <Helmet>
        <title>{event.title}{event.subtitle ? ` — ${event.subtitle}` : ''} | Serava Sportsevents</title>
        <meta name="description" content={event.description} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${event.title}${event.subtitle ? ` — ${event.subtitle}` : ''} | Serava Sportsevents`} />
        <meta property="og:description" content={event.description} />
        <meta property="og:image" content={event.image_url} />
        <meta property="og:url" content={`https://seravasports.com/event/${event.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${event.title} | Serava Sportsevents`} />
        <meta name="twitter:description" content={event.description} />
        <meta name="twitter:image" content={event.image_url} />
      </Helmet>
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end pb-16 md:pb-24">
        <div className="absolute inset-0">
          <img src={event.image_url} alt={event.title} className={`w-full h-full ${event.image_fit === "contain" ? "object-contain bg-black p-12 md:p-24" : "object-cover"}`} />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
          <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 text-white/50 hover:text-[#C5A059] text-sm font-['Outfit'] mb-8 transition-colors"><ArrowLeft size={16} /> {t("back_to_events")}</Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex flex-wrap gap-2 mb-4">
              {event.tags.map(tag => <span key={tag} className="event-tag px-3 py-1 bg-black/60 backdrop-blur-sm text-white/90 border border-white/10">{tag}</span>)}
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white mb-2">{event.title}</h1>
            {event.subtitle && <p className="text-[#C5A059] text-lg md:text-xl font-['Outfit'] font-light mb-6">{event.subtitle}</p>}
            <div className="flex flex-wrap items-center gap-6 text-white/60 font-['Outfit'] text-sm">
              <span className="flex items-center gap-2"><Calendar size={16} className="text-[#C5A059]/70" />{event.date}</span>
              <span className="flex items-center gap-2"><MapPin size={16} className="text-[#C5A059]/70" />{event.location}</span>
              {event.capacity && <span className="flex items-center gap-2"><Users size={16} className="text-[#C5A059]/70" />{event.capacity} {t("places")}</span>}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Description */}
        <section className="py-16 md:py-24 grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("about_event")}</span>
            <p className="text-white/70 font-['Outfit'] font-light text-base leading-relaxed mb-6">{event.long_description || event.description}</p>
            {event.date_details && <div className="flex items-start gap-3 p-4 bg-[#141414] border border-[#262626]"><Clock size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" /><p className="text-white/60 font-['Outfit'] font-light text-sm">{event.date_details}</p></div>}
          </div>
          {event.whats_included?.length > 0 && (
            <div className="bg-[#141414] border border-[#262626] p-8">
              <h3 className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-['Outfit'] font-semibold mb-6">{t("whats_included")}</h3>
              <div className="space-y-3">{event.whats_included.map(item => <div key={item} className="flex items-start gap-3"><Check size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" /><span className="text-white/60 text-sm font-['Outfit'] font-light">{item}</span></div>)}</div>
            </div>
          )}
        </section>

        <div className="section-divider" />

        {/* Packages */}
        {event.packages?.length > 0 && (
          <section data-testid="packages-section" className="py-16 md:py-24">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("select_experience")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight text-white mb-12">{t("packages")}</h2>
            <div className="space-y-6">
              {event.packages.map((pkg, i) => (
                <PackageCard key={`${pkg.name || 'pkg'}-${i}`} pkg={pkg} index={i} quantity={quantities[i] || 1}
                  onQuantityChange={(q) => setQuantities({ ...quantities, [i]: q })}
                  onSelect={(p, method) => handlePackageSelect(p, method, i)} />
              ))}
            </div>
          </section>
        )}

        <div className="section-divider" />

        {/* Schedule */}
        {event.schedule?.length > 0 && (
          <section className="py-16 md:py-24">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("event_timeline")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-light text-white mb-10">{t("schedule")}</h2>
            <div className="space-y-0">{event.schedule.map((s, i) => <div key={`${s.time || ''}-${s.activity || ''}-${i}`} className="flex gap-6 py-4 border-b border-[#262626] last:border-0"><span className="text-[#C5A059] font-['Outfit'] font-medium text-sm w-24 flex-shrink-0">{s.time}</span><span className="text-white/60 font-['Outfit'] font-light text-sm">{s.activity}</span></div>)}</div>
          </section>
        )}

        {/* FAQ */}
        {event.faq?.length > 0 && (
          <section className="py-16 md:py-24">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("qa_label")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-light text-white mb-10">{t("faq")}</h2>
            <div className="space-y-0">{event.faq.map((item, i) => (
              <div key={`${item.question || 'faq'}-${i}`} className="border-b border-[#262626] last:border-0">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between py-5 text-left group">
                  <span className="text-white/80 font-['Outfit'] font-light text-base group-hover:text-white transition-colors pr-4">{item.question}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-[#C5A059] flex-shrink-0" /> : <ChevronDown size={18} className="text-white/30 flex-shrink-0" />}
                </button>
                {openFaq === i && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="pb-5"><p className="text-white/50 font-['Outfit'] font-light text-sm leading-relaxed">{item.answer}</p></motion.div>}
              </div>
            ))}</div>
          </section>
        )}

        <div className="section-divider" />

        {/* Enquiry */}
        <section id="event-enquiry" className="py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("interested_label")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light text-white mb-4">{t("send_enquiry")}</h2>
            <p className="text-white/50 font-['Outfit'] font-light text-sm mb-10 leading-relaxed">{t("enquiry_intro")}</p>
            <EnquiryForm eventId={event.id} eventTitle={event.title} />
          </div>
        </section>

        <div className="section-divider" />

        {/* Related */}
        {relatedEvents.length > 0 && (
          <section className="py-16 md:py-24">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("more_experiences")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-light text-white mb-10">{t("related_events")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{relatedEvents.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
          </section>
        )}
      </div>

      {/* Checkout Modal */}
      {checkoutPkg && event && (
        <CheckoutModal
          event={event}
          pkg={checkoutPkg}
          pkgIndex={checkoutPkgIndex}
          quantity={quantities[checkoutPkgIndex] || 1}
          onClose={() => setCheckoutPkg(null)}
        />
      )}
    </main>
  );
}
